import React, { useCallback, useMemo, useState } from "react";

import { push } from "connected-react-router";
import Button from "@material-ui/core/Button";
import ButtonGroup from "@material-ui/core/ButtonGroup";
import Typography from "@material-ui/core/Typography";
import { reducerType } from "redux/reducers";
import { connect } from "react-redux";
import { Dispatch } from "redux";
import HeaderBarSub from "components/HeaderBarSub";
import { useTranslation } from "react-i18next";
import GridFullHeight from "components/GridFullHeight";
import GridFormContainer from "components/GridFormContainer";
import clsx from "clsx";
import styles from "./styles";
import Container from "@material-ui/core/Container";
import AsyncSelect from "react-select/async";
import { getUsersAPI } from "api/services/getUsers";
// Shared with MyWork (same UI)
import ActivityTab from "../MyWork/components/ActivityTab";
import BadgesTab from "../MyWork/components/BadgesTab";
import StatisticsTab from "../MyWork/components/StatisticsTab";
import CertificatesTab from "../MyWork/components/CertificatesTab";
import { Theme, useTheme } from "@material-ui/core/styles";

interface VolunteerOption {
  value: string;
  label: string;
}

interface MyWorkProps {
  dispatch: Dispatch;
}

const getVolunteerListParams = (keyword = "") => ({
  sort: "createdAt",
  direction: "desc",
  limit: 100,
  skip: 0,
  filter: "roles",
  roles: "VOLUNTEER LEAD,VOLUNTEER,VOLUNTEER SENIOR",
  q: keyword.trim(),
  search: "email,name,displayName",
});

const buildVolunteerSelectStyles = (theme: Theme) => {
  const primary = theme.palette.primary.main;
  const fontFamily = theme.typography.fontFamily;
  const fontSize = theme.typography.body2?.fontSize ?? 14;
  const text = {
    fontFamily,
    fontSize,
  };
  return {
    control: (provided: Record<string, unknown>, state: { isFocused: boolean }) => ({
      ...provided,
      border: `1px solid ${primary}`,
      backgroundColor: "#f5f5f5",
      boxShadow: state.isFocused ? `0 0 0 1px ${primary}` : "none",
      minHeight: 36,
      fontFamily,
      fontSize,
      "&:hover": {
        borderColor: primary,
      },
    }),
    placeholder: (provided: Record<string, unknown>) => ({
      ...provided,
      ...text,
      color: theme.palette.text.hint,
    }),
    input: (provided: Record<string, unknown>) => ({
      ...provided,
      ...text,
    }),
    option: (provided: Record<string, unknown>) => ({
      ...provided,
      ...text,
    }),
  };
};

const mapStateToProps = (state: reducerType) => {
  return {
    loading: state.service.loading,
  };
};

const ManageVolunteersWork = React.memo((props: MyWorkProps) => {
  const { dispatch } = props;
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [selectedVolunteer, setSelectedVolunteer] = useState<VolunteerOption | null>(
    null
  );
  const classes = styles();
  const theme = useTheme();
  const volunteerSelectStyles = useMemo(
    () => buildVolunteerSelectStyles(theme),
    [theme]
  );
  const translate = useTranslation().t;

  const tabs = ["STATISTICS", "ACTIVITY", "BADGES", "CERTIFICATES"];

  const loadVolunteerOptions = useCallback(async (inputValue: string) => {
    try {
      const users = await getUsersAPI(getVolunteerListParams(inputValue));
      const list = Array.isArray(users) ? users : [];
      return list
        .map((u: { id?: string; displayName?: string; email?: string }) => {
          const label =
            [u.displayName, u.email].filter(Boolean).join(" - ") ||
            u.email ||
            u.id ||
            "";
          return { value: String(u.id ?? ""), label };
        })
        .filter((o) => o.value.length > 0 && o.label.length > 0);
    } catch {
      return [];
    }
  }, []);

  const openUrl = (url) => {
    dispatch(push(url));
  };

  return (
    <GridFullHeight container>
      <GridFormContainer item xs={12} sm={12} md={12}>
        <GridFullHeight container>
          <HeaderBarSub
            openUrl={openUrl}
            isSearch={false}
            name={translate("MANAGE_VOLUNTEERS_WORK")}
          />
          <Container className={classes.root}>
            <div className={classes.volunteerSelectRow}>
              <Typography variant="body2" className={classes.volunteerSelectLabel}>
                {translate("SELECT_VOLUNTEER")}
              </Typography>
              <div className={classes.volunteerSelectWrap}>
                <AsyncSelect<VolunteerOption>
                  cacheOptions
                  defaultOptions
                  styles={volunteerSelectStyles}
                  placeholder={translate("PH_SEARCH_VOLUNTEERS")}
                  isClearable
                  value={selectedVolunteer}
                  onChange={(opt) => setSelectedVolunteer(opt)}
                  loadOptions={loadVolunteerOptions}
                  noOptionsMessage={() => translate("NO_VOLUNTEERS")}
                  loadingMessage={() => translate("LOADING")}
                />
              </div>
            </div>
            <ButtonGroup
              className={classes.groupBtns}
              variant="contained"
              color="primary"
              aria-label="contained primary button group"
            >
              {tabs.map((tab, idx) => (
                <Button
                  key={idx}
                  onClick={() => setSelectedIndex(idx)}
                  className={clsx(
                    {
                      [classes.btnActive]: idx === selectedIndex,
                    },
                    classes.btnFilter
                  )}
                >
                  {translate(tab)}
                </Button>
              ))}
            </ButtonGroup>
          </Container>

          {/* Content */}
          {selectedVolunteer && (
            <Container className={classes.root}>
              {selectedIndex === 0 && (
                <StatisticsTab userId={selectedVolunteer.value} />
              )}
              {selectedIndex === 1 && (
                <ActivityTab userId={selectedVolunteer.value} />
              )}
              {selectedIndex === 2 && (
                <BadgesTab userId={selectedVolunteer.value} />
              )}
              {selectedIndex === 3 && (
                <CertificatesTab userId={selectedVolunteer.value} />
              )}
            </Container>
          )}
        </GridFullHeight>
      </GridFormContainer>
    </GridFullHeight>
  );
});

export default connect(mapStateToProps)(ManageVolunteersWork);
